// Modal admin para revisar atestados + justificativas pendentes.
// Chamado pelo badge "Notificações" na página CartaoPonto.

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, FileText, Clock, Check, XCircle, Loader2, User, History, Download } from "lucide-react";
import { AvisosColeta } from "@/components/AvisosColeta";
import { AvisosAparelhos } from "@/components/AvisosAparelhos";
import { Smartphone } from "lucide-react";

const fmtDt = (s) => (s ? new Date(s).toLocaleString("pt-BR") : "—");

function HistoricoDecisoes() {
  const [itens, setItens] = useState(null);
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState("todos");
  useEffect(() => {
    api.get("/decisoes-historico").then((r) => setItens(r.data || [])).catch(() => { setItens([]); toast.error("Erro ao carregar histórico"); });
  }, []);
  if (itens === null) return <div className="text-center text-slate-400 py-8"><Loader2 size={18} className="animate-spin mx-auto" /></div>;
  const lista = itens.filter((i) => (filtro === "todos" || i.status === filtro || i.tipo === filtro)
    && (!q || `${i.funcionario_nome} ${i.decidido_por}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="space-y-2" data-testid="historico-decisoes">
      <div className="flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar funcionário ou quem decidiu"
          data-testid="hist-busca" className="flex-1 min-w-[180px] h-9 px-3 rounded-lg border border-slate-300 text-sm" />
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)} data-testid="hist-filtro"
          className="h-9 px-2 rounded-lg border border-slate-300 text-sm bg-white">
          <option value="todos">Todos</option><option value="aceito">Aceitos</option><option value="recusado">Recusados</option>
          <option value="atestado">Atestados</option><option value="justificativa">Justificativas</option>
        </select>
      </div>
      {lista.length === 0 ? <div className="text-center text-slate-400 py-8 text-sm" data-testid="hist-vazio">Nenhuma decisão registrada.</div> : (
        <ul className="space-y-2">
          {lista.map((i) => (
            <li key={i.id} className="border border-slate-200 rounded-lg p-3 text-sm" data-testid={`hist-item-${i.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${i.status === "aceito" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`} data-testid={`hist-status-${i.id}`}>{i.status === "aceito" ? "ACEITO" : "RECUSADO"}</span>
                <span className="text-[10px] uppercase text-slate-500 font-semibold">{i.tipo}</span>
                <span className="font-semibold text-slate-900">{i.funcionario_nome}</span>
                <span className="text-xs text-slate-500">{i.empresa_nome}</span>
              </div>
              <div className="text-xs text-slate-600 mt-1">{i.referencia}{i.detalhe ? ` • ${i.detalhe}` : ""}</div>
              <div className="text-xs text-slate-500 mt-1" data-testid={`hist-quem-${i.id}`}>
                Decidido por <strong>{i.decidido_por || "—"}</strong> em {fmtDt(i.decidido_em)} • enviado em {fmtDt(i.enviado_em)}
              </div>
              {i.motivo && <div className="text-[11px] italic text-slate-500 mt-0.5">Motivo: "{i.motivo}"</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RevisaoNotificacoes({ onClose, onChange, abaInicial = "atestados", coletasNovas = 0, aparelhosPendentes = 0 }) {
  const [tab, setTab] = useState(abaInicial);
  const [atestados, setAtestados] = useState([]);
  const [justificativas, setJustificativas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {kind, item}
  const [decidindo, setDecidindo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [verFoto, setVerFoto] = useState(null);
  // 🆕 Opções do admin ao aceitar atestado: abonar + horas:minutos abonados
  const [abonar, setAbonar] = useState(false);
  const [horasAbonadas, setHorasAbonadas] = useState("");       // só horas (int)
  const [minutosAbonados, setMinutosAbonados] = useState("");   // só minutos (0-59)

  const load = async () => {
    setLoading(true);
    try {
      const [a, j] = await Promise.all([
        api.get("/atestados-pendentes"), api.get("/justificativas-ponto-pendentes"),
      ]);
      setAtestados(a.data || []);
      setJustificativas(j.data || []);
    } catch (e) { toast.error("Erro ao carregar — os pendentes continuam salvos; tente novamente"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const decidir = async (decision) => {
    if (!preview) return;
    setDecidindo(true);
    try {
      const url = preview.kind === "atestado"
        ? `/atestados/${preview.item.id}`
        : `/justificativas-ponto/${preview.item.id}`;
      // 🆕 Para atestados aceitos, passa opções de abono (abonar + horas:minutos)
      const body = { decision, motivo };
      if (preview.kind === "atestado" && decision === "aceito") {
        body.abonar = !!abonar;
        // Converte HH + MM → horas decimais (ex: 4h 30min → 4.5)
        const h = parseInt((horasAbonadas || "0").toString(), 10);
        const m = parseInt((minutosAbonados || "0").toString(), 10);
        const hDec = (Number.isNaN(h) ? 0 : h) + (Number.isNaN(m) ? 0 : m) / 60;
        if (hDec > 0) body.atestado_horas = Number(hDec.toFixed(4));
      }
      try {
        await api.patch(url, body);
        toast.success(`${preview.kind === "atestado" ? "Atestado" : "Justificativa"} ${decision}`);
      } catch (e) {
        if (e.response?.status !== 409) throw e;
        toast.info(e.response.data.detail);
      }
      setPreview(null); setMotivo(""); setAbonar(false);
      setHorasAbonadas(""); setMinutosAbonados("");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setDecidindo(false); }
  };

  const total = atestados.length + justificativas.length;
  const abrirRevisao = (kind, item) => {
    setPreview({ kind, item }); setMotivo("");
    api.post(`/${kind === "atestado" ? "atestados" : "justificativas-ponto"}/${item.id}/visualizar`).catch(() => {});
  };
  const Pendente = ({ item }) => (
    <span className="inline-flex items-center gap-1 mt-1">
      <span className="px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[10px] font-bold" data-testid={`status-pendente-${item.id}`}>PENDENTE</span>
      {item.visualizado_em && <span className="text-[10px] text-slate-500" data-testid={`visualizado-${item.id}`}>Visualizado em {new Date(item.visualizado_em).toLocaleString("pt-BR")} — aguardando decisão</span>}
    </span>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-revisao">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Notificações pendentes</h2>
            <p className="text-xs text-slate-500">{total} item{total !== 1 ? "s" : ""} aguardando sua revisão</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button onClick={() => setTab("atestados")}
            data-testid="tab-atestados"
            className={`px-4 h-10 text-sm font-semibold -mb-px border-b-2 ${tab === "atestados" ? "border-amber-500 text-amber-700" : "border-transparent text-slate-500"}`}>
            <FileText size={13} className="inline mr-1" /> Atestados ({atestados.length})
          </button>
          <button onClick={() => setTab("justificativas")}
            data-testid="tab-justificativas"
            className={`px-4 h-10 text-sm font-semibold -mb-px border-b-2 ${tab === "justificativas" ? "border-blue-500 text-blue-700" : "border-transparent text-slate-500"}`}>
            <Clock size={13} className="inline mr-1" /> Justificativas ({justificativas.length})
          </button>
          <button onClick={() => setTab("historico")}
            data-testid="tab-historico"
            className={`px-4 h-10 text-sm font-semibold -mb-px border-b-2 ${tab === "historico" ? "border-slate-700 text-slate-900" : "border-transparent text-slate-500"}`}>
            <History size={13} className="inline mr-1" /> Histórico
          </button>
          <button onClick={() => setTab("coletas")}
            data-testid="tab-coletas"
            className={`px-4 h-10 text-sm font-semibold -mb-px border-b-2 ${tab === "coletas" ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500"}`}>
            <Download size={13} className="inline mr-1" /> Coletas{coletasNovas > 0 ? ` (${coletasNovas})` : ""}
          </button>
          <button onClick={() => setTab("aparelhos")}
            data-testid="tab-aparelhos"
            className={`px-4 h-10 text-sm font-semibold -mb-px border-b-2 ${tab === "aparelhos" ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500"}`}>
            <Smartphone size={13} className="inline mr-1" /> Aparelhos{aparelhosPendentes > 0 ? ` (${aparelhosPendentes})` : ""}
          </button>
        </div>

        <div className="p-4">
          {tab === "aparelhos" && <AvisosAparelhos onChange={onChange} />}
          {tab === "coletas" && <AvisosColeta onLidos={onChange} />}
          {tab === "historico" && <HistoricoDecisoes key={atestados.length + justificativas.length} />}
          {loading && !["historico", "coletas", "aparelhos"].includes(tab) && <div className="text-center text-slate-400 py-8"><Loader2 size={18} className="animate-spin mx-auto" /></div>}

          {!loading && tab === "atestados" && (
            atestados.length === 0 ? (
              <div className="text-center text-slate-400 py-8 text-sm">Sem atestados pendentes. 🎉</div>
            ) : (
              <ul className="space-y-2">
                {atestados.map((a) => (
                  <li key={a.id} className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3" data-testid={`atestado-${a.id}`}>
                    <div className="h-10 w-10 rounded-full bg-amber-100 text-amber-700 grid place-items-center">
                      <FileText size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-900">{a.funcionario?.nome}</div>
                      <div className="text-xs text-slate-600">
                        {a.data_inicio}{a.data_fim !== a.data_inicio ? ` → ${a.data_fim}` : ""} • {a.dias_afastamento} dia{a.dias_afastamento !== 1 ? "s" : ""}
                        {a.cid ? ` • CID ${a.cid}` : ""}
                      </div>
                      {a.observacao && <div className="text-[11px] text-slate-500 italic mt-0.5">"{a.observacao}"</div>}
                      <Pendente item={a} />
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setVerFoto(a.id)} disabled={a.tem_foto === false}
                        data-testid={`btn-ver-foto-${a.id}`}
                        className="h-8 px-2 rounded bg-white border border-slate-300 text-xs">Ver foto</button>
                      <button onClick={() => abrirRevisao("atestado", a)}
                        data-testid={`btn-revisar-${a.id}`}
                        className="h-8 px-3 rounded bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold">
                        Revisar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )
          )}

          {!loading && tab === "justificativas" && (
            justificativas.length === 0 ? (
              <div className="text-center text-slate-400 py-8 text-sm">Sem justificativas pendentes.</div>
            ) : (
              <ul className="space-y-2">
                {justificativas.map((j) => (
                  <li key={j.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3" data-testid={`just-${j.id}`}>
                    <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-700 grid place-items-center">
                      <Clock size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-900">{j.funcionario?.nome}</div>
                      <div className="text-xs text-slate-600">
                        {j.data} às <strong>{j.hora}</strong> •
                        <span className="ml-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-mono text-[10px] font-bold">
                          {j.slot_detectado}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500 italic mt-0.5 line-clamp-2">"{j.motivo}"</div>
                      <Pendente item={j} />
                    </div>
                    <button onClick={() => abrirRevisao("justificativa", j)}
                      data-testid={`btn-revisar-just-${j.id}`}
                      className="h-8 px-3 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold">
                      Revisar
                    </button>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>

        {/* Modal decisão */}
        {preview && (
          <div className="fixed inset-0 bg-slate-900/80 z-50 flex items-center justify-center p-4" onClick={() => !decidindo && setPreview(null)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" data-testid="dialog-decisao">
              <h3 className="text-lg font-bold">
                {preview.kind === "atestado" ? "Revisar atestado" : "Revisar justificativa"}
              </h3>
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <div className="font-semibold">{preview.item.funcionario?.nome}</div>
                {preview.kind === "atestado" ? (
                  <>
                    <div className="text-xs text-slate-600 mt-1">
                      {preview.item.data_inicio} → {preview.item.data_fim} ({preview.item.dias_afastamento} dias)
                      {preview.item.cid ? ` • CID ${preview.item.cid}` : ""}
                    </div>
                    {preview.item.foto_base64 ? null : <div className="text-xs text-slate-500 italic">Sem foto visível aqui — use "Ver foto" na lista</div>}
                  </>
                ) : (
                  <>
                    <div className="text-xs text-slate-600 mt-1">
                      {preview.item.data} às <strong>{preview.item.hora}</strong> →{" "}
                      <span className="font-mono bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded text-[10px] font-bold">
                        {preview.item.slot_detectado}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 mt-2 italic">"{preview.item.motivo}"</div>
                  </>
                )}
              </div>
              {/* 🆕 Opção Abonar + HH:MM (somente para atestados) */}
              {preview.kind === "atestado" && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={abonar}
                      onChange={(e) => setAbonar(e.target.checked)}
                      data-testid="chk-abonar"
                      className="h-4 w-4 accent-emerald-600" />
                    <span className="text-sm font-semibold text-emerald-900">
                      Abonar as horas desse(s) dia(s)
                    </span>
                  </label>
                  <div>
                    <span className="text-xs font-semibold text-slate-700 block">
                      Tempo abonado por dia (opcional — padrão = carga diária)
                    </span>
                    <div className="mt-1 flex items-end gap-2">
                      <label className="block flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Horas</span>
                        <input
                          type="number" min="0" max="24" step="1" inputMode="numeric"
                          value={horasAbonadas}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                            setHorasAbonadas(v);
                          }}
                          disabled={!abonar}
                          data-testid="input-horas-abonadas"
                          placeholder="0"
                          className="mt-0.5 w-full h-10 px-3 rounded-lg border border-slate-300 outline-none text-sm font-mono text-center disabled:bg-slate-100 disabled:text-slate-400"
                        />
                      </label>
                      <div className="text-slate-500 font-bold pb-2">:</div>
                      <label className="block flex-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Minutos</span>
                        <input
                          type="number" min="0" max="59" step="1" inputMode="numeric"
                          value={minutosAbonados}
                          onChange={(e) => {
                            let v = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                            if (v && Number(v) > 59) v = "59";
                            setMinutosAbonados(v);
                          }}
                          disabled={!abonar}
                          data-testid="input-minutos-abonados"
                          placeholder="0"
                          className="mt-0.5 w-full h-10 px-3 rounded-lg border border-slate-300 outline-none text-sm font-mono text-center disabled:bg-slate-100 disabled:text-slate-400"
                        />
                      </label>
                    </div>
                    <p className="text-[10.5px] text-slate-500 mt-1">
                      Pode preencher só minutos (ex: <code className="bg-slate-100 px-1 rounded">0h 30min</code>)
                      ou só horas (<code className="bg-slate-100 px-1 rounded">4h 0min</code>).
                      Em branco = usa a carga horária do funcionário.
                    </p>
                  </div>
                </div>
              )}

              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Motivo da decisão (opcional)</span>
                <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)}
                  rows={2}
                  data-testid="input-motivo-decisao"
                  placeholder="Ex: documento sem assinatura médica; falta de clareza no CID…"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm" />
              </label>
              <div className="flex gap-2 pt-1">
                <button onClick={() => decidir("recusado")} disabled={decidindo}
                  data-testid="btn-recusar"
                  className="flex-1 h-11 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-50">
                  <XCircle size={14} /> Recusar
                </button>
                <button onClick={() => decidir("aceito")} disabled={decidindo}
                  data-testid="btn-aceitar"
                  className="flex-1 h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-50">
                  <Check size={14} /> Aceitar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Foto do atestado em tela cheia */}
        {verFoto && (
          <AtestadoFotoViewer id={verFoto} onClose={() => { setVerFoto(null); load(); }} />
        )}
      </div>
    </div>
  );
}

function AtestadoFotoViewer({ id, onClose }) {
  const [foto, setFoto] = useState(null);
  useEffect(() => {
    api.get(`/atestados/${id}/foto`).then((r) => setFoto(r.data?.foto_base64 || "")).catch(() => setFoto(""));
  }, [id]);
  return (
    <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-2" onClick={onClose} data-testid="foto-viewer">
      <button onClick={onClose} className="absolute top-3 right-3 h-10 w-10 rounded-full bg-slate-900/80 text-white grid place-items-center z-10">
        <X size={20} />
      </button>
      {foto === null ? <Loader2 size={32} className="animate-spin text-white" /> : foto ? (
        <img src={foto} alt="Atestado" className="max-w-full max-h-full object-contain" />
      ) : <div className="text-white text-sm">Foto não disponível</div>}
    </div>
  );
}
